import React from 'react';
import { 
  FileText, 
  Image as ImageIcon, 
  CheckCircle, 
  CalendarClock, 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  AlertTriangle 
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { InspectionRecordStub } from '../../models/property';
import workAnalyzerService from '../../services/workAnalyzerService';

interface PropertyInspectionListProps {
  inspections: InspectionRecordStub[];
  selectedInspectionId: string | null;
  onSelectInspection: (inspectionId: string) => void;
  inspectionTypes: Record<string, 'PDF' | 'Image' | undefined>;
}

const PropertyInspectionList: React.FC<PropertyInspectionListProps> = ({
  inspections,
  selectedInspectionId,
  onSelectInspection,
  inspectionTypes
}) => {
  const [statusMap, setStatusMap] = React.useState<Record<string, string>>({});
  
  // Fetch status for each inspection when component mounts
  React.useEffect(() => {
    const fetchStatuses = async () => {
      const statusData: Record<string, string> = {};
      
      for (const inspection of inspections) {
        try {
          // Use the status from the inspection record if it exists, otherwise fetch it
          if (inspection.status) {
            statusData[inspection.inspectionId] = inspection.status;
          } else {
            const status = await workAnalyzerService.getInspectionStatus(inspection.inspectionId);
            statusData[inspection.inspectionId] = status;
          }
        } catch (error) {
          console.error(`Error fetching status for inspection ${inspection.inspectionId}:`, error);
          statusData[inspection.inspectionId] = 'ERROR';
        }
      }
      
      setStatusMap(statusData);
    };
    
    fetchStatuses();
  }, [inspections]);
  
  if (!inspections || inspections.length === 0) {
    return (
      <div className="text-center my-4">
        <p className="text-muted-foreground">
          No inspection records found
        </p>
      </div>
    );
  }

  // Sort inspections by date (newest first)
  const sortedInspections = [...inspections].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Get status badge for each inspection
  const getStatusBadge = (inspectionId: string) => {
    const status = statusMap[inspectionId];
    
    if (!status) {
      return null;
    }
    
    const isProcessing = ['PROCESSING', 'PAGES_GENERATED', 'ANALYZING', 'SYNTHESIZING', 'PENDING'].includes(status);
    const isComplete = status === 'COMPLETED';
    const isPartial = ['COMPLETED_WITH_ERRORS', 'PARTIAL'].includes(status);
    const isFailed = ['FAILED', 'SYNTHESIS_FAILED', 'ERROR'].includes(status);
    
    if (isProcessing) {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Processing</span>
        </Badge>
      );
    } else if (isComplete) {
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          <span>Complete</span>
        </Badge>
      );
    } else if (isPartial) {
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          <span>Partial</span>
        </Badge>
      );
    } else if (isFailed) {
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          <span>Failed</span>
        </Badge>
      );
    }
    
    return null;
  };

  return (
    <Card className="overflow-hidden">
      <div className="divide-y">
        {sortedInspections.map((inspection, index) => (
          <div 
            key={inspection.inspectionId}
            className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
              inspection.inspectionId === selectedInspectionId ? 'bg-slate-50' : ''
            }`}
            onClick={() => onSelectInspection(inspection.inspectionId)}
          >
            <div className="flex items-center">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mr-3">
                {inspectionTypes[inspection.inspectionId] === 'PDF' ? (
                  <FileText className={`h-5 w-5 ${inspection.inspectionId === selectedInspectionId ? 'text-primary' : 'text-gray-500'}`} />
                ) : (
                  <ImageIcon className={`h-5 w-5 ${inspection.inspectionId === selectedInspectionId ? 'text-primary' : 'text-gray-500'}`} />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <h4 className={`text-base ${inspection.inspectionId === selectedInspectionId ? 'font-medium' : 'font-normal'}`}>
                    Inspection #{sortedInspections.length - index}
                  </h4>
                  
                  <div className="flex items-center gap-2">
                    <Badge 
                      className={inspectionTypes[inspection.inspectionId] === 'PDF' 
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' 
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-100'
                      }
                      variant="outline"
                    >
                      {inspectionTypes[inspection.inspectionId] || 'Unknown'}
                    </Badge>
                    
                    {/* Status Badge */}
                    <div className="flex-shrink-0">
                      {getStatusBadge(inspection.inspectionId)}
                    </div>
                    
                    {inspection.inspectionId === selectedInspectionId && (
                      <CheckCircle className="h-4 w-4 text-primary ml-1" />
                    )}
                  </div>
                </div>
                
                <div className="flex items-center text-sm text-muted-foreground mt-1">
                  <CalendarClock className="h-3 w-3 mr-1" />
                  <span>{formatDate(inspection.date)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default PropertyInspectionList;